/**
 * The Assignment page's right-side drawers (AIC-312b, overlay-only since AIC-485, the drawer standard since AIC-503):
 * contacts, the invitation, interview links, the link history, the spec look-up and the assignment's details, on
 * ../components/Drawer.tsx DrawerFrame, with an explicit Close and Esc. Every one is a look-up that covers the page and
 * nothing behind it moves:
 * - A modal dialog at every width: role=dialog and aria-modal, a scrim over the page, the rest of the app inert (every
 *   sibling on the way up from the drawer to <body>), Tab and Shift+Tab kept inside, and the page's scroll locked
 *   (the Lightbox's shared, ref-counted lock, so the two can't collide). The scrim closes it, as Close, Esc and the
 *   browser's Back do (a history entry per open drawer, ../components/Drawer.tsx useBackCloses). Full screen on a phone.
 *
 * - One drawer at a time. Opening another closes the first. Opened from a control inside the open drawer (links →
 *   history), the new drawer keeps the first one's opener, so closing it goes back to the page, not to a control that
 *   has gone. Opened from the page, the new opener replaces the old one.
 * - Closing (Close, Esc, the scrim, Back, or opening another) sends focus back to the control that opened it; when that
 *   control has gone, to the material row's button for that drawer, else the materials card.
 * - Unsaved edits (a contact form, a reassign reason) ask first, through the confirm lightbox; "Keep editing" goes back
 *   into the drawer, where the close was asked from.
 * - Leaving the page with unsaved edits asks the same (AIC-325): in-app navigation (the Back link, a result link, the
 *   sidebar) and browser Back and Forward, through the router's navigation blocker (../router.tsx, which says what it
 *   can't catch), and the browser's own beforeunload prompt, registered only while there are unsaved edits. "Keep
 *   editing" returns focus to the link that tried to leave, else into the drawer.
 * - A control inside the drawer that goes away while it has focus (Expire, once the link is expired) leaves focus on the
 *   drawer's heading rather than nowhere.
 * Nothing here calls the API.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type MutableRefObject, type ReactNode } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { DrawerFrame, drawerEscape, headingOf, useBackCloses } from "../components/Drawer.tsx";
import { inertAround, keepTabInside } from "../components/modalLayer.ts";
import { firstFocusable, pageFallback, usePanelOpener } from "../components/Panel.tsx";
import { lockScroll } from "../components/scrollLock.ts";
import { useNavigationBlocker } from "../router.tsx";

export type DrawerKey = "contacts" | "invite" | "links" | "history" | "facts" | "spec";

/** Each drawer's region id: the #assignment-* focus target it stands for. */
export const DRAWER_ID: Record<DrawerKey, string> = {
  contacts: "assignment-contacts", invite: "assignment-invite", links: "assignment-links", history: "assignment-history", facts: "assignment-facts", spec: "assignment-spec",
};

/** The drawer a #hash (or an in-page link's target) names, if any. */
export function drawerOf(hash: string): DrawerKey | null {
  const id = decodeURIComponent(hash.replace(/^#/, ""));
  return (Object.keys(DRAWER_ID) as DrawerKey[]).find((k) => DRAWER_ID[k] === id) ?? null;
}

export interface Drawers {
  readonly open: DrawerKey | null;
  /** Opens `key` from `from` (null: the page opened it, from a #hash); focus goes to its heading. Open already: focuses it. */
  show(key: DrawerKey, from: HTMLElement | null): void;
  /** Close (the control or Esc): asks first when there are unsaved edits. */
  close(): void;
  /** Closes without asking (after an action that leaves nothing unsaved), and hands focus to `to` instead of the opener. */
  closeTo(to: () => HTMLElement | null | undefined): void;
  /** The open drawer's contents report unsaved edits. */
  setDirty(dirty: boolean): void;
}

interface DrawerState extends Drawers {
  focusKey: number;
  panelRef: MutableRefObject<HTMLElement | null>;
  asking: boolean;
  /** What the lightbox asks about: leaving the page (AIC-325), rather than closing or switching the drawer. */
  leaving: boolean;
  confirmDiscard(): void;
  keepEditing(): void;
  onFocus(e: FocusEvent<HTMLElement>): void;
  onBlur(e: FocusEvent<HTMLElement>): void;
  onKeyDown(e: KeyboardEvent<HTMLElement>): void;
  hostRef: (el: HTMLDivElement | null) => void;
}

/** Where focus goes when a drawer's opener has gone: that drawer's row button when it is on show, else the page's fallback. */
const fallbackFor = (key: DrawerKey) => () =>
  firstFocusable(document.querySelector<HTMLElement>(`[data-opens="${key}"]`)) ?? pageFallback();

export function useDrawers(): DrawerState {
  const [open, setOpen] = useState<DrawerKey | null>(null);
  const [focusKey, setFocusKey] = useState(0);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const opener = usePanelOpener();
  const panelRef = useRef<HTMLElement | null>(null);
  const dirty = useRef(false);
  const openRef = useRef<DrawerKey | null>(null);
  openRef.current = open;
  // Focus was inside the drawer: if the focused control goes away, the heading takes focus (see below).
  const inside = useRef(false);
  const modal = open !== null; // (a drawer is always a modal overlay)
  // The page behind a drawer does not scroll (shared with the lightboxes, so the two can't collide).
  useEffect(() => {
    if (!modal) return;
    return lockScroll();
  }, [modal]);
  // The drawer's host (scrim, panel and its lightbox): what stays usable while the rest of the app is inert. Kept as
  // state, so the inert background goes whenever the host does, whatever unmounted it (a drawer the page stops
  // rendering while it is still "open", as the invite's when the invite becomes the task's).
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (!modal || !host) return;
    return inertAround(host);
  }, [modal, host, open]);

  // Esc closes the drawer even when focus has been left on the page's body (the control that had it went away, e.g. a
  // button replaced by its result): the drawer's own key handler never sees that key. A key from a control, a dialog or
  // a lightbox is theirs.
  const closeRef = useRef<() => void>(() => undefined);
  const backRef = useRef<() => boolean>(() => true);
  useEffect(() => {
    if (!modal) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (e.target !== document.body && e.target !== document.documentElement) return;
      // A lightbox, a confirm or a pending decision on top has the key; the drawer under it stays.
      if (document.querySelector(".ipd-lb, dialog[open]")) return;
      e.preventDefault();
      closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modal]);

  // Browser Back closes the open drawer (asking first about unsaved edits).
  useBackCloses(modal, () => backRef.current());

  // AIC-325: unsaved edits as state too, for beforeunload (registered only while they are unsaved).
  const [unsaved, setUnsaved] = useState(false);
  const markDirty = (d: boolean) => {
    dirty.current = d;
    setUnsaved(d);
  };
  // What the lightbox asks about: closing (or switching) the drawer, or leaving the page.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);
  // The lightbox is up (set at once when a leave asks, so a second Back before the next render sees it too).
  const lightbox = useRef(false);
  lightbox.current = pending !== null;
  // What the router checks is the ref, set as the form reports: a Back pressed before the next render still asks.
  useNavigationBlocker(() => dirty.current, (attempt) => {
    // Already asking (a second Back while the lightbox is up): the router has put the URL back again; the question
    // already asked, its answer and where focus is stay as they are.
    if (lightbox.current) return;
    lightbox.current = true;
    // Before the lightbox opens, focus goes where "Keep editing" should return it (the lightbox gives focus back to
    // what had it): the control that tried to navigate, else where it is in the drawer, else the drawer's heading.
    const panel = panelRef.current;
    const at = document.activeElement;
    if (attempt.from?.isConnected) attempt.from.focus({ preventScroll: true });
    else if (!(at instanceof HTMLElement && panel?.contains(at))) headingOf(panel)?.focus({ preventScroll: true });
    setLeaving(true);
    setPending(() => () => {
      markDirty(false);
      attempt.proceed();
    });
  });

  const ask = (then: () => void) => {
    if (dirty.current) {
      setLeaving(false);
      setPending(() => then);
    } else then();
  };
  const doOpen = (key: DrawerKey, from: HTMLElement | null) => {
    const current = openRef.current;
    const fromInside = !!from && !!panelRef.current?.contains(from);
    if (current === null || !fromInside) {
      opener.take();
      if (from) opener.remember(from, { open: false, inside: panelRef.current });
    }
    markDirty(false);
    inside.current = false;
    setOpen(key);
    setFocusKey((n) => n + 1);
  };
  const doClose = (to?: () => HTMLElement | null | undefined) => {
    const key = openRef.current;
    if (!key) return;
    markDirty(false);
    inside.current = false;
    setOpen(null);
    if (to) {
      opener.take();
      setTimeout(() => (to() ?? fallbackFor(key)())?.focus(), 0);
    } else {
      opener.restore(fallbackFor(key));
    }
  };

  // After any render: when the control that had focus inside the drawer went away (it isn't rendered any more),
  // focus would be left on the page's body. The drawer's heading takes it instead.
  useLayoutEffect(() => {
    if (!open || !inside.current) return;
    const at = document.activeElement;
    if (!at || at === document.body) headingOf(panelRef.current)?.focus({ preventScroll: true });
  });

  closeRef.current = () => ask(() => doClose());
  // Back: false while it asks about unsaved edits first (the drawer stays, and so does the history entry for it).
  backRef.current = () => { if (dirty.current) { ask(() => doClose()); return false; } doClose(); return true; };
  return {
    open, focusKey, panelRef, hostRef: setHost, asking: pending !== null, leaving,
    show(key, from) {
      if (openRef.current === key) {
        headingOf(panelRef.current)?.focus();
        return;
      }
      ask(() => doOpen(key, from));
    },
    close() { ask(() => doClose()); },
    closeTo(to) { doClose(to); },
    setDirty(d) { markDirty(d); },
    confirmDiscard() {
      const then = pending;
      setPending(null);
      then?.();
    },
    keepEditing() {
      setPending(null);
      // Back into the drawer: the dialog returns focus to where the close was asked from; when that was nowhere (a
      // browser that doesn't focus a clicked button), the drawer's heading.
      setTimeout(() => {
        const at = document.activeElement;
        if (openRef.current && (!at || at === document.body)) headingOf(panelRef.current)?.focus();
      }, 0);
    },
    onFocus() { inside.current = true; },
    onKeyDown(e) {
      // Tab and Shift+Tab go round inside the drawer.
      keepTabInside(e, panelRef.current);
    },
    onBlur(e) {
      // Moving to somewhere else on the page: focus is no longer the drawer's to look after.
      const to = e.relatedTarget as Node | null;
      const host = e.currentTarget;
      if (to && !host.contains(to)) { inside.current = false; return; }
      if (!to) {
        // Nowhere: a click on the page's background (the control is still there), or the control went away.
        const left = e.target as HTMLElement;
        setTimeout(() => {
          if (left.isConnected) inside.current = false;
          else if (inside.current && openRef.current && (!document.activeElement || document.activeElement === document.body)) headingOf(panelRef.current)?.focus({ preventScroll: true });
        }, 0);
      }
    },
  };
}

/**
 * The open drawer: a DrawerFrame with the drawer's id, heading and Close; switching drawers keeps the frame (one scrim, one
 * slide-in) and mounts the new contents, which take focus. `wide` is the link history's and the spec's: their columns at a
 * readable width.
 */
export function Drawer({ drawers, id, headingId, title, sub, closeLabel, wide = false, children }: {
  drawers: DrawerState; id: DrawerKey; headingId: string; title: string; sub?: ReactNode; closeLabel: string; wide?: boolean; children: ReactNode;
}) {
  return (
    <DrawerFrame
      id={DRAWER_ID[id]} headingId={headingId} title={title} sub={sub} closeLabel={closeLabel} wide={wide}
      className={`ad-drawer${wide ? " ad-drawer--wide" : ""}`} onClose={drawers.close} focusKey={drawers.focusKey} panelRef={drawers.panelRef}
      hostRef={drawers.hostRef} hostProps={{ onFocus: drawers.onFocus, onBlur: drawers.onBlur, onKeyDown: drawers.onKeyDown }}
      panelProps={{ onKeyDown: (e) => drawerEscape(e, drawers.panelRef.current, drawers.close) }}
      // In the host, so it stays usable while the page behind a modal drawer is inert.
      aside={<DiscardDialog drawers={drawers} what={title} />}
    >
      <Fragment key={id}>{children}</Fragment>
    </DrawerFrame>
  );
}

/** The "discard unsaved changes?" lightbox for a drawer's close, or for leaving the page while it has unsaved edits. */
function DiscardDialog({ drawers, what }: { drawers: DrawerState; what: string }) {
  return (
    <ConfirmDialog
      open={drawers.asking}
      title="Discard your changes?"
      body={`What you entered in ${what} isn't saved yet. ${drawers.leaving ? "Leaving the page" : "Closing the drawer"} loses it; nothing has been changed.`}
      confirmLabel="Discard changes"
      cancelLabel="Keep editing"
      onConfirm={drawers.confirmDiscard}
      onCancel={drawers.keepEditing}
    />
  );
}
